import { writable } from 'svelte/store';

const initialState = {
    BaseColumnChart: {
        credits: {
            enabled: false,
        },
        title: {
            text: "",
            align: "center",
        },
        chart: {
            type: "column",
        },
        xAxis: {
            title: null,
            categories: [],
            type: "category",
        },
        yAxis: {
            title: { text: "" },
        },
        series: [],
        legend: {
            enabled: true,
        },
        plotOptions: {
            series: {
                stacking: null,
                dataLabels: {
                    enabled: true,
                    overflow: "justify",
                },
            },
        },
        tooltip: {
            pointFormat: '{point.name}: <b>{point.y:,.2f} ha</b>'
        },
    },
    BaseStackColumnChart: {
        credits: {
            enabled: false,
          },
          title: {
            text: "",
            align: "center",
          },
          chart: {
            type: "column",
          },
          xAxis: {
            title: null,
            categories: [],
            type: "category",
          },
          yAxis: {
            title: { text: "" },
          },
          series: [],
          legend: {
            enabled: true,
          },
          plotOptions: {
            series: {
              stacking: "normal",
              dataLabels: {
                enabled: false,
                overflow: "justify",
              },
            },
          },
    },
    BaseBarChart: {
        credits: {
            enabled: false,
          },
          title: {
            text: "",
            align: "center",
          },
          chart: {
            type: "bar",
          },
          xAxis: {
            type: "category",
          },
          yAxis: {
            title: {
              text: "",
            },
          },
          series: [],
          plotOptions: {
            series: {
              dataLabels: {
                enabled: true,
                overflow: "justify",
              },
            },
          },
    },
    SankeyDiagramChart: {
        // ... same as your Vue store
    },
    DependencyWheelChart: {
        // ... same as your Vue store
    },
    BasePieChart: {
        credits: {
            enabled: false,
          },
          title: {
            text: "",
            align: "center",
          },
          chart: {
            type: "pie",
          },
          series: [],
    },
    LineStockChart: {
        // ... same as your Vue store
    }
};

function createChartStore() {
    const { subscribe, set, update } = writable(initialState);

    const highChartColors = [
        "#B89623", "#B77856", "#B44444", "#9A924B", "purple", "brown"
    ];

    return {
        subscribe,
        // Getters converted to methods
        getStockColumnChartData: (rawData) => {
            let preData = JSON.parse(JSON.stringify(initialState.LineStockChart));
            if (rawData && rawData.series.length > 0) {
                preData.title.text = rawData.title;
                preData.series = rawData.series;
                preData.series[0].type = "column";
            }
            return preData;
        },

        getColumnChartData: (rawData) => {
            let preData = JSON.parse(JSON.stringify(initialState.BaseColumnChart));
            if (rawData && rawData.series) {
                preData.title.text = rawData.title;
                if (rawData.plotOptions) {
                    preData.plotOptions = {...preData.plotOptions, ...rawData.plotOptions};
                }
                const unit = rawData.unit || '';
                preData.tooltip = {
                    pointFormat: `{point.name}: <b>{point.y:,.2f}${unit ? ' ' + unit : ''}</b>`
                };
                
                for (let [seriesName, seriesData] of Object.entries(rawData.series)) {
                    // Create array of data points
                    const dataPoints = Object.entries(seriesData).map((x) => ({
                        name: x[0],
                        y: x[1],
                        color: rawData.colorMap?.[x[0]]
                    }));

                    // Default sorting enabled unless explicitly disabled in plotOptions
                    const shouldSort = !(rawData.plotOptions?.series?.dataSorting?.enabled === false);
                    
                    if (shouldSort) {
                        // Sort data points by value
                        dataPoints.sort((a, b) => b.y - a.y);
                        // Update categories to match sorted data
                        preData.xAxis.categories = dataPoints.map(point => point.name);
                    } else {
                        // Keep original order for categories
                        preData.xAxis.categories = Object.keys(seriesData);
                    }
                    // console.log("rawData.plotOptions?.series?.showInLegend", rawData.plotOptions.series.showInLegend)
                    preData.series.push({
                        showInLegend: rawData.plotOptions?.series?.showInLegend ?? true,
                        name: seriesName,
                        data: dataPoints
                    });
                }
            }
            return preData;
        },

        getPieChartData: (rawData) => {
            let preData = JSON.parse(JSON.stringify(initialState.BasePieChart));
            if (rawData && rawData.series) {
                preData.title.text = rawData.title;
                for (let [seriesName, seriesData] of Object.entries(rawData.series)) {
                    let tempData = Object.entries(seriesData).map(([key, value]) => ({
                        name: key,
                        y: value
                    }));
                    preData.series.push({
                        name: seriesName,
                        data: tempData,
                    });
                }
            }
            return preData;
        },

        getStackColumnChartData: (rawData) => {
            let preData = JSON.parse(JSON.stringify(initialState.BaseStackColumnChart));
            if (rawData && rawData.series) {
                preData.title.text = rawData.title;
                preData.title.align = rawData.titleAlign || "center";
                
                if (rawData.categories) {
                    preData.xAxis.categories = rawData.categories;
                }
                
                if (rawData.yAxis) {
                    preData.yAxis = {
                        ...preData.yAxis,
                        ...rawData.yAxis,
                        stackLabels: {
                            enabled: false
                        }
                    };
                }
                
                if (rawData.legend) {
                    preData.legend = {
                        ...preData.legend,
                        ...rawData.legend
                    };
                }
                
                const unit = rawData.unit ? ` ${rawData.unit}` : '';
                preData.tooltip = {
                    headerFormat: '<b>{point.key}</b><br/>',
                    pointFormat: `{series.name}: <b>{point.y:,.2f}${unit}</b><br/>Total: <b>{point.stackTotal:,.2f}${unit}</b>`
                };
                
                preData.series = rawData.series.map(series => ({
                    name: series.name,
                    data: series.data.map(value => Number(value)),
                    color: rawData.colorMap?.[series.name]
                }));
            }
            return preData;
        },

        getLineChartData: (rawData) => {
            let preData = {
                credits: {
                    enabled: false,
                },
                title: {
                    text: rawData.title || "",
                    align: rawData.titleAlign || "center",
                },
                chart: {
                    type: "line",
                },
                xAxis: {
                    categories: rawData.categories || [],
                    title: rawData.xAxisTitle ? { text: rawData.xAxisTitle } : null,
                },
                yAxis: {
                    title: rawData.yAxisTitle ? { text: rawData.yAxisTitle } : { text: "" },
                },
                series: [],
                legend: {
                    enabled: true,
                },
                plotOptions: {
                    series: {
                        dataLabels: {
                            enabled: true,
                            overflow: "justify",
                        },
                    },
                },
                tooltip: {
                    pointFormat: '{series.name}: <b>{point.y}</b>'
                },
            };

            if (rawData && rawData.series) {
                preData.series = rawData.series.map(series => ({
                    name: series.name,
                    data: series.data.map(value => Number(value))
                }));
            }

            return preData;
        },

        getBarChartData: (rawData) => {
            let preData = JSON.parse(JSON.stringify(initialState.BaseBarChart));
            if (rawData && rawData.series) {
                preData.title.text = rawData.title;
                if (rawData.plotOptions) {
                    preData.plotOptions = {...preData.plotOptions, ...rawData.plotOptions};
                }
                const unit = rawData.unit || '';
                preData.tooltip = {
                    pointFormat: `{point.name}: <b>{point.y:,.2f}${unit ? ' ' + unit : ''}</b>`
                };
                if (rawData.yAxis) {
                    preData.yAxis = rawData.yAxis;
                }
                let index = 0;
                for (let [seriesName, seriesData] of Object.entries(rawData.series)) {
                    preData.series.push({
                        name: seriesName,
                        data: Object.entries(seriesData).map((x) => ({
                            name: x[0],
                            y: x[1],
                            color: rawData.colorMap?.[x[0]]
                        })),
                        dataSorting: {
                            enabled: true,
                            sortKey: "y",
                        },
                    });
                    index++;
                }
            }
            return preData;
        },

        // Add other getter methods similarly...

        reset: () => set(initialState)
    };
}

export const chartStore = createChartStore();
